import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { validEmail, defaultTeammateMessage, isEmptyString } from 'Shared/helpers';
import { bulkCreateWorkspaces } from 'Shared/actions/workspaces';
import TrackingConstants from 'Shared/constants/Tracking.constants';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import useValidateEmployeeEmail from './use-validate-employee-email';
import { useTranslation } from 'Shared/contexts/TranslationsContext';

type Params = {
  isSubmitting: boolean;
  initialEmail?: string;
  setIsSubmitting: Function;
};

export function useAddTeammate({ isSubmitting, setIsSubmitting, initialEmail = '' }: Params) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { isPiOrganization } = useOrganization();
  const { isEmployeeEmailOrValidDomain } = useValidateEmployeeEmail();
  const { tr } = useTranslation();

  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    email: initialEmail,
    personalMessage: defaultTeammateMessage,
  });

  const isEmptyTeammateName = isEmptyString(formValues.firstName) || isEmptyString(formValues.lastName);

  const isInvalidEmail = useCallback(() => {
    if (!formValues.email) return false;
    return !validEmail(formValues.email);
  }, [formValues.email]);

  const isAllowedPiDomainEmail = useCallback(() => {
    if (!formValues.email) return false;
    return !isEmployeeEmailOrValidDomain({ email: formValues.email });
  }, [formValues.email]);

  const disabledSubmit =
    isSubmitting || isEmptyTeammateName || isInvalidEmail() || (isPiOrganization && isAllowedPiDomainEmail());

  const handleTextFieldsChange = event => setFormValues({ ...formValues, [event.target.name]: event.target.value });

  const handleSubmit = async ({ isRedirectTo = true } = {}) => {
    if (isEmptyString(formValues.firstName)) return WpToastManager.danger(tr('perform.teammates.first_name_blank'));
    if (isEmptyString(formValues.lastName)) return WpToastManager.danger(tr('perform.teammates.last_name_blank'));
    setIsSubmitting(true);
    const email = formValues.email;
    const teammateName = `${formValues.firstName} ${formValues.lastName}`;
    const firstCreatedWorkspace = await dispatch(
      bulkCreateWorkspaces({
        workspacesParams: [
          { name: teammateName, inviteeEmails: [email || null], personalMessage: formValues.personalMessage },
        ],
        inviteMethod: TrackingConstants.INVITE_METHOD_TYPES.INVITE_FROM_USER_MANAGEMENT,
      })
    );
    if (isRedirectTo && firstCreatedWorkspace?.path) history.push(firstCreatedWorkspace?.path);
    return setIsSubmitting(false);
  };

  return {
    formValues,
    onTextFieldsChange: handleTextFieldsChange,
    disabledSubmit,
    onSubmit: handleSubmit,
    isInvalidEmail,
    setFormValues,
  };
}
